<template>
  <div class="board-col">
    <div class="board-col__header" :class="{ static: hideAddButton }">
      <v-tooltip top :disabled="!showTitleTooltip">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="board-col__header--title text-ellipsis">
            {{ column.title }}
          </div>
        </template>
        <span>{{ column.title }}</span>
      </v-tooltip>

      <v-btn
        v-if="!hideAddButton"
        class="board-col__add-button"
        color="appMain"
        small
        @click="onItemCreate"
      >
        Add New
      </v-btn>

      <v-chip
        small
        label
        :color="column.color || 'primary'"
        class="count-chip light--text"
      >
        {{ count }}
      </v-chip>
    </div>
    <div class="board-col__loading">
      <v-progress-linear indeterminate v-if="loading" />
    </div>

    <v-btn
      small
      text
      color="primary"
      v-if="page !== 1"
      @click="flipPage(-1)"
      class="mb-2"
    >
      &laquo; Page {{ page - 1 }}
    </v-btn>

    <div
      class="board-col__list draggable mb-4"
      :id="column.key"
      ref="boardColumnList"
    >
      <board-card
        v-for="item in items"
        class="mb-2"
        :key="item.id"
        :item="item"
        :id="item.id"
        :data-id="item.id"
        :data-store-key="column.key"
        :controls="controls"
        @toggleItemSelection="$emit('toggleItemSelection', item.id)"
        @editItem="$emit('editItem', item)"
      />
    </div>

    <v-btn
      v-if="pages > 1"
      small
      text
      color="primary"
      :disabled="!hasNextPage"
      @click="flipPage(1)"
    >
      Page {{ page + 1 }} &raquo;
    </v-btn>
  </div>
</template>

<script>
import { Sortable } from 'sortablejs';
import { eventBus } from '@/lib/events';
import { isTextOverflow } from '@/filters/helpers';

import BoardCard from '@/components/board/BoardCard';

export default {
  name: 'BoardColumn',
  components: {
    BoardCard,
  },
  props: {
    column: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
    controls: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
    itemsCount: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    pages: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideAddButton: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showTitleTooltip: false,
      sortableClient: null,
    };
  },
  computed: {
    count() {
      return this.itemsCount || this.items?.length;
    },
    hasNextPage() {
      return this.pages > this.page;
    },
  },
  methods: {
    flipPage(direction) {
      let page = this.page + direction;
      this.$emit('changePage', page);
    },
    onItemCreate() {
      this.$emit('createItem', {
        key: this.column?.key,
      });
    },
    onItemAdd(e) {
      this.$emit('addItem', {
        key: this.column?.key,
        items: this.extractItemIds(e),
      });

      this.handleItemsOrderChange();
    },
    onItemRemove(e) {
      this.$emit('removeItem', {
        key: e.to?.id,
        items: this.extractItemIds(e),
      });

      this.handleItemsOrderChange();
    },
    onItemUpdate() {
      this.handleItemsOrderChange();
    },
    sortItems({ key, items }) {
      if (key !== this.column?.key || !items.length) return;

      setTimeout(() => {
        this.sortableClient.sort(items);
      }, 0);
    },
    async handleItemsOrderChange() {
      await this.$nextTick();
      const itemsList = [];

      const items = this.$refs.boardColumnList.querySelectorAll('.board-card');

      items.forEach((el) => {
        itemsList.push(el.id);
      });

      this.$emit('reorderItems', {
        key: this.column?.key,
        items: itemsList,
      });

      // fix items order
      this.sortItems({
        key: this.column?.key,
        items: itemsList,
      });
    },
    extractItemIds(e) {
      const { items, item, newIndex, newIndicies } = e || {};
      const singleItem = !items.length && item;
      let itemsId = [];

      if (singleItem) {
        itemsId = [
          {
            id: item.id,
            storeKey: item.dataset.storeKey,
            index: newIndex,
          },
        ];
      } else {
        itemsId = items.map((i, index) => {
          return {
            id: i.id || '',
            storeKey: i.dataset.storeKey,
            index: newIndicies[index].index,
          };
        });
      }

      return [...new Set(itemsId)];
    },
    initSortable() {
      if (!this.editable) return;

      try {
        this.sortableClient = new Sortable(this.$refs.boardColumnList, {
          multiDrag: true,
          animation: 150,
          ghostClass: 'board-card__ghost',
          selectedClass: 'selected',
          group: 'board-items',
          sort: this.sort,
          onAdd: (e) => this.onItemAdd(e),
          onRemove: (e) => this.onItemRemove(e),
          onUpdate: (e) => this.onItemUpdate(e),
          onEnd(e) {
            for (let i in e.items) {
              Sortable.utils.deselect(e.items[i]);
            }
          },
        });

        let deselectMultiDrag =
          this.sortableClient.multiDrag._deselectMultiDrag;
        this.sortableClient.options.avoidImplicitDeselect = true;
        document.removeEventListener('pointerup', deselectMultiDrag, false);
        document.removeEventListener('mouseup', deselectMultiDrag, false);
        document.removeEventListener('touchend', deselectMultiDrag, false);
      } catch (e) {
        console.log('Error:', e);
      }
    },

    async isTitleTextOverflow() {
      await this.$nextTick();

      if (!this.$el) return;
      const el = this.$el.querySelector('.board-col__header--title');
      this.showTitleTooltip = isTextOverflow(el);
    },
  },
  mounted() {
    this.isTitleTextOverflow();
    this.initSortable();
    eventBus.on('updateItemsOrder', this.sortItems);
  },
};
</script>
